// src/pages/Orders/RefundApproval.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrderByIdForAdminThunk,
  approveRefundThunk,
  rejectRefundThunk,
} from "../../store/thunks/orderThunks";
import Button from "../../components/common/Button";
import Badge from "../../components/common/Badge";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  RefreshIcon,
  ExclamationCircleIcon,
  UserIcon,
  CurrencyRupeeIcon,
  DocumentTextIcon,
} from "@heroicons/react/outline";
import { notifySuccess, notifyError } from "../../utils/notification";

const RefundApproval = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { order = null, loading = false } = useSelector(
    (state) => state.orders || {}
  );

  const [formData, setFormData] = useState({
    approvalNotes: "",
    internalNotes: "",
  });

  const [rejectionData, setRejectionData] = useState({
    reason: "",
    alternativeAction: "",
  });

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (orderId) {
      dispatch(getOrderByIdForAdminThunk(orderId));
    }
  }, [orderId, dispatch]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleApprove = async () => {
    if (!window.confirm("Are you sure you want to approve this refund?")) {
      return;
    }

    setProcessing(true);
    try {
      await dispatch(
        approveRefundThunk({
          orderId: order.orderId,
          approvalNotes: formData.approvalNotes,
          internalNotes: formData.internalNotes,
        })
      ).unwrap();

      notifySuccess("Refund approved successfully");
      navigate(`/orders/${orderId}`);
    } catch (error) {
      notifyError(error || "Failed to approve refund");
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionData.reason.trim()) {
      notifyError("Please provide a rejection reason");
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to reject this refund? This action cannot be undone."
      )
    ) {
      return;
    }

    setProcessing(true);
    try {
      await dispatch(
        rejectRefundThunk({
          orderId: order.orderId,
          reason: rejectionData.reason,
          alternativeAction: rejectionData.alternativeAction,
        })
      ).unwrap();

      notifySuccess("Refund rejected");
      navigate(`/orders/${orderId}`);
    } catch (error) {
      notifyError(error || "Failed to reject refund");
      setProcessing(false);
    }
  };

  if (loading || !order) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const refund = order.refundDetails;

  if (!refund || !refund.required) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <ExclamationCircleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No Refund Required
          </h2>
          <p className="text-gray-600 mb-4">
            This order does not require a refund.
          </p>
          <Button onClick={() => navigate(`/orders/${orderId}`)}>
            Back to Order
          </Button>
        </div>
      </div>
    );
  }

  if (refund.refundStatus !== "REFUND_PENDING_APPROVAL") {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Refund Already {refund.refundStatus}
          </h2>
          <p className="text-gray-600 mb-4">
            This refund has already been processed.
          </p>
          <Button onClick={() => navigate(`/orders/${orderId}`)}>
            Back to Order
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/orders/${orderId}`)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Order Details
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-orange-100 rounded-lg">
            <RefreshIcon className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Approve Refund - {order.orderId}
            </h1>
            <p className="text-sm text-gray-600">
              Review and approve refund request
            </p>
          </div>
        </div>
      </div>

      {/* Warning Alert */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
        <div className="flex">
          <ExclamationCircleIcon className="w-5 h-5 text-yellow-400 mr-3 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-yellow-800">
              Manager Approval Required
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              This refund requires approval because the amount exceeds ₹10,000.
              Please review all details carefully before approving.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Refund Summary Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
            <h2 className="text-lg font-semibold text-white">Refund Summary</h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Refund Amount */}
              <div className="md:col-span-2 bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-6 border-2 border-orange-200">
                <p className="text-sm text-orange-700 mb-1">Refund Amount</p>
                <p className="text-4xl font-bold text-orange-600">
                  {formatCurrency(refund.refundAmount)}
                </p>
              </div>

              {/* Refund Details */}
              <div>
                <p className="text-sm text-gray-600 mb-1">Refund Method</p>
                <p className="font-semibold text-gray-900">
                  {refund.refundMethod}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Refund Reason</p>
                <p className="font-semibold text-gray-900">
                  {refund.refundReason}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Current Status</p>
                <Badge variant="yellow" size="lg">
                  {refund.refundStatus}
                </Badge>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Initiated At</p>
                <p className="font-semibold text-gray-900">
                  {formatDate(refund.initiatedAt)}
                </p>
              </div>
            </div>

            {/* Bank Details */}
            {refund.refundMethod === "BANK_TRANSFER" && refund.bankDetails && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Bank Account Details
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-600">Account Name</p>
                    <p className="font-medium text-gray-900">
                      {refund.bankDetails.accountName}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Account Number</p>
                    <p className="font-medium text-gray-900">
                      {refund.bankDetails.accountNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">IFSC Code</p>
                    <p className="font-medium text-gray-900">
                      {refund.bankDetails.ifscCode}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Bank Name</p>
                    <p className="font-medium text-gray-900">
                      {refund.bankDetails.bankName}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* UPI Details */}
            {refund.refundMethod === "UPI" && refund.upiDetails && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  UPI Details
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-600">UPI ID</p>
                      <p className="font-medium text-gray-900">
                        {refund.upiDetails.vpa}
                      </p>
                    </div>
                    {refund.upiDetails.verifiedName && (
                      <div>
                        <p className="text-xs text-gray-600">Verified Name</p>
                        <p className="font-medium text-gray-900">
                          {refund.upiDetails.verifiedName}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Customer Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UserIcon className="w-5 h-5" />
            Customer Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Name</p>
              <p className="font-semibold text-gray-900">
                {order.userId?.name || order.guestCustomer?.name || "Guest"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Phone</p>
              <p className="font-semibold text-gray-900">
                {order.userId?.phoneNumber ||
                  order.guestCustomer?.phoneNumber ||
                  "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-semibold text-gray-900">
                {order.userId?.email || order.guestCustomer?.email || "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Account Type</p>
              <p className="font-semibold text-gray-900">
                {order.userId?.accountType || "B2C"}
              </p>
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DocumentTextIcon className="w-5 h-5" />
            Order Summary
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Order Total</span>
              <span className="font-semibold">
                {formatCurrency(order.orderSummary?.finalTotals?.grandTotal)}
              </span>
            </div>
            <div className="flex justify-between py-2 text-green-600">
              <span>Exchange Value</span>
              <span className="font-semibold">
                -
                {formatCurrency(
                  order.exchangeItems?.reduce(
                    (sum, item) => sum + (item.agreedValue || 0),
                    0
                  ) || 0
                )}
              </span>
            </div>
            <div className="flex justify-between py-2 border-t font-semibold text-orange-600">
              <span>Refund Due to Customer</span>
              <span>{formatCurrency(refund.refundAmount)}</span>
            </div>
          </div>
        </div>

        {/* Approval Form */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Approval Details
          </h2>

          <div className="space-y-4">
            {/* Approval Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Approval Notes (Optional)
              </label>
              <textarea
                value={formData.approvalNotes}
                onChange={(e) =>
                  setFormData({ ...formData, approvalNotes: e.target.value })
                }
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Add any notes about this approval (visible to customer)..."
              />
            </div>

            {/* Internal Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Internal Notes (Optional)
              </label>
              <textarea
                value={formData.internalNotes}
                onChange={(e) =>
                  setFormData({ ...formData, internalNotes: e.target.value })
                }
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Add internal notes (not visible to customer)..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Internal notes are only visible to staff members
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(`/orders/${orderId}`)}
              disabled={processing}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => setShowRejectModal(true)}
              disabled={processing}
              className="flex-1 flex items-center justify-center gap-2"
            >
              <XCircleIcon className="w-5 h-5" />
              Reject Refund
            </Button>
            <Button
              variant="success"
              onClick={handleApprove}
              disabled={processing}
              className="flex-1 flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  <RefreshIcon className="w-5 h-5 animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-5 h-5" />
                  Approve Refund
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="bg-red-600 px-6 py-4 rounded-t-xl">
              <h3 className="text-lg font-semibold text-white">
                Reject Refund Request
              </h3>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-gray-600">
                Please provide a reason for rejecting this refund request.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason *
                </label>
                <textarea
                  value={rejectionData.reason}
                  onChange={(e) =>
                    setRejectionData({
                      ...rejectionData,
                      reason: e.target.value,
                    })
                  }
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Explain why this refund is being rejected..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alternative Action (Optional)
                </label>
                <textarea
                  value={rejectionData.alternativeAction}
                  onChange={(e) =>
                    setRejectionData({
                      ...rejectionData,
                      alternativeAction: e.target.value,
                    })
                  }
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Suggest alternative resolution..."
                />
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex gap-3 rounded-b-xl">
              <Button
                variant="outline"
                onClick={() => setShowRejectModal(false)}
                disabled={processing}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleReject}
                disabled={processing || !rejectionData.reason.trim()}
                className="flex-1"
              >
                {processing ? "Rejecting..." : "Reject Refund"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundApproval;
